import { clamp, inRange, Responsive, scoped, Snap } from 'vevet';
import { MOBILE_BREAKPOINT, VINYL_CAROUSEL_SETTINGS } from './settings';
import { MouseMarquee } from './MouseMarquee';
import { isMobile } from './isMobile';
import { AutoChange } from './AutoChange';
const settings = VINYL_CAROUSEL_SETTINGS;

class BannerVinyl extends HTMLElement {
	constructor() {
		super(...arguments);
		Object.defineProperty(this, '_eventsEmitter', {
			enumerable: true,
			configurable: true,
			writable: true,
			value: void 0,
		});
		Object.defineProperty(this, '_snap', {
			enumerable: true,
			configurable: true,
			writable: true,
			value: void 0,
		});
		Object.defineProperty(this, '_mouseMarquee', {
			enumerable: true,
			configurable: true,
			writable: true,
			value: void 0,
		});
		Object.defineProperty(this, '_autoChange', {
			enumerable: true,
			configurable: true,
			writable: true,
			value: void 0,
		});
		Object.defineProperty(this, '_isVisible', {
			enumerable: true,
			configurable: true,
			writable: true,
			value: false,
		});
		Object.defineProperty(this, '_observer', {
			enumerable: true,
			configurable: true,
			writable: true,
			value: null,
		});
	}

	connectedCallback() {
		this._eventsEmitter = this.parentElement;
		this._snap = new Snap({
			container: this,
			eventsEmitter: this._eventsEmitter,
			duration: settings.duration,
			gap: '-17vw',
			grabCursor: true,
			centered: true,
			freemode: 'sticky',
			loop: true,
			direction: 'vertical',
			wheel: false,
			swipe: false,
			swipeLerp: 0.5,
			swipeInertiaRatio: 0.3,
			shortSwipesDuration: 30,
			shortSwipesThreshold: 1,
		});

		this._handlePropsUpdate();

		this._snap.on('props', this._handlePropsUpdate.bind(this));

		new Responsive(this._snap, [
			{
				at: `@media (width < ${MOBILE_BREAKPOINT}px)`,
				props: {
					direction: 'horizontal',
					gap: 0,
					swipe: true,
					freemode: 'sticky',
					swipeInertiaDuration: (distance) => clamp(distance, 1000, 5000),
				},
			},
		]);
		this._setEvents();
		this._waitForImages().then(() => {
			this._hideLoader();
			window.requestAnimationFrame(() => {
				this._forceResize();
				this.setAttribute('data-ready', 'true');
			});
		});
	}
	_handlePropsUpdate() {
		this._eventsEmitter?.classList.toggle('swipable', this._snap?.props.swipe);
	}
	_waitForImages() {
		const images = Array.from(this.querySelectorAll('img'));
		if (images.length === 0) return Promise.resolve();

		const loadPromises = images.map(
			(img) =>
				new Promise((resolve) => {
					if (img.complete && img.naturalHeight !== 0) {
						resolve();
					} else {
						img.addEventListener('load', resolve, { once: true });
						img.addEventListener('error', resolve, { once: true });
					}
				}),
		);

		return Promise.all(loadPromises);
	}

	_hideLoader() {
		const loader = this.parentElement.parentElement.querySelector('.banner-vinyl__loader-wrapper');
		if (loader) {
			loader.style.transition = 'opacity 0.4s ease';
			loader.style.opacity = '0';
			setTimeout(() => {
				loader.style.display = 'none';
			}, 400);
		}
	}
	_setEvents() {
		if (!this._snap || !this._eventsEmitter) {
			return;
		}
		this._mouseMarquee = new MouseMarquee(this._eventsEmitter, this._handleMouseMarquee.bind(this));
		this._autoChange = new AutoChange(this._snap);
		this._observer = new IntersectionObserver(this._handleObserver.bind(this));
		this._observer.observe(this);
		this._snap.on('resize', this._toggle.bind(this), { timeout: 500 });
		this._snap.on('swipeStart', this._handleSwipeStart.bind(this));
		this._snap.on('swipeEnd', this._handleSwipeEnd.bind(this));
		this._snap.on('swipeInertiaEnd', this._handleSwipeInertiaEnd.bind(this));
		this._snap.on('swipeInertiaFail', this._handleSwipeInertiaEnd.bind(this));
		this._snap.on('update', () => this._render());
	}
	destroy() {
		this._mouseMarquee?.destroy();
		this._autoChange?.destroy();
		this._observer?.disconnect();
		this._observer = null;
		this._snap?.destroy();
		this._snap = undefined;
	}
	_forceResize() {
		this._snap?.resize(false);
		this._snap?.slides.forEach((slide) => slide.resize(false));
	}
	_handleMouseMarquee() {
		const mouseMarquee = this._mouseMarquee;
		const snap = this._snap;
		if (!mouseMarquee || !snap || snap.props.swipe) {
			return;
		}
		const step = mouseMarquee.value * snap.domSize * settings.mouseSpeed;
		snap.track.set(snap.track.current + step);
		snap.render();
	}
	_handleObserver(entries) {
		entries.forEach((entry) => {
			this._isVisible = entry.isIntersecting;
		});
		this._toggle();
	}
	_toggle() {
		if (this._isVisible) {
			this._mouseMarquee?.enable();
			this._autoChange?.enable();
		} else {
			this._mouseMarquee?.disable();
			this._autoChange?.disable();
		}
	}
	_handleSwipeStart() {
		this._mouseMarquee?.disable();
		this._autoChange?.disable();
	}
	_handleSwipeEnd() {
		if (this._isVisible) {
			this._mouseMarquee?.enable();
		}
	}
	_handleSwipeInertiaEnd() {
		if (this._autoChange) {
			this._autoChange?.enable();
		}
	}
	_render() {
		if (isMobile()) {
			this._renderMobile();
		} else {
			this._renderDesktop();
		}
	}
	_renderDesktop() {
		if (!this._snap) {
			return;
		}
		const { slides } = this._snap;
		slides.forEach((slide) => {
			const { element, coord, size, progress } = slide;
			const zIndex = -Math.round(progress * 1000);
			element.style.transform = `
		translate3d(-50%, ${coord}px, ${size * -1}px)
		rotateX(${-settings.desktopRotation}deg)
	  `;
			element.style.top = '0';
			element.style.left = '50%';
			element.style.zIndex = `${zIndex}`;
		});
	}
	_renderMobile() {
		if (!this._snap) {
			return;
		}
		const { slides } = this._snap;
		slides.forEach((slide) => {
			const { element, coord, size, progress } = slide;
			const zIndexProgress = scoped(progress, 0, 0.5);
			const zIndexAbsProgress = Math.abs(zIndexProgress);
			const zIndex = Math.round(zIndexAbsProgress * 1000) * -1;
			const defaultRotation = settings.mobileRotation;
			let rotation = 0;
			if (progress === 0) {
				rotation = defaultRotation * progress;
			} else if (inRange(progress, -1, 1)) {
				rotation = defaultRotation * progress;
			} else {
				const currentProgress = Math.abs(progress) - 1;
				const sp = scoped(currentProgress, 0, 1);
				rotation = defaultRotation + (95 - defaultRotation) * sp;
				rotation *= Math.sign(progress);
			}
			const gapProgress = clamp(Math.abs(progress) - 1, 0, Infinity);
			const gap = gapProgress * size * settings.mobileGap * Math.sign(progress);
			const zProgress = progress;
			const z = Math.abs(zProgress) * size * -settings.mobileZAdjoin;
			element.style.transform = `
		translate3d(${coord + gap}px, -50%, ${z}px)
		rotateY(${rotation}deg)
	  `;
			element.style.top = '50%';
			element.style.left = '0';
			element.style.zIndex = `${zIndex}`;
		});
	}
}

customElements.define('vinyl-carousel', BannerVinyl);

export default BannerVinyl;
